import axios from "axios";
import { Mutex } from "async-mutex";
import { logout } from "./api";

const baseURL = process.env.REACT_APP_BASE_API;
axios.defaults.headers.common["Content-Type"] = "application/json";
const mutex = new Mutex();

const CookieUtils = {
  set: (name, value, days = 1) => {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${encodeURIComponent(
      JSON.stringify(value)
    )};expires=${expires.toUTCString()};path=/;SameSite=Strict;${
      window.location.protocol === "https:" ? "Secure" : ""
    }`;
  },

  get: (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        try {
          return JSON.parse(
            decodeURIComponent(c.substring(nameEQ.length, c.length))
          );
        } catch (e) {
          return null;
        }
      }
    }
    return null;
  },
  remove: (name) => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;SameSite=Strict;${
      window.location.protocol === "https:" ? "Secure" : ""
    }`;
  },
    removeAll: () => {
    const cookies = document.cookie.split("; ");
    for (let i = 0; i < cookies.length; i++) {
      const key = cookies[i].split("=")[0];
      document.cookie = `${key}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Strict;${
        window.location.protocol === "https:" ? "Secure" : ""
      }`;
    }
  }
};
const getAuthToken = () => {
  try {
    let token = CookieUtils.get("token");
    return token;
  } catch (error) {
    console.error("Error getting auth token:", error);
    return null;
  }
};

const sessionLogout = () => {
  try {
    localStorage.clear();
    CookieUtils.remove("token");
    CookieUtils.remove("clientName");
    return new Promise((resolve) => {
      window.location.href = "/login";
      resolve();
    });
  } catch (error) {}
};

const onRetry = async (req) => {
  const { config } = req;

  const token = getAuthToken();
  const options = {
    method: config.method,
    url: config.url,
    headers: config.headers,
    data: config.data,
  };
  options.headers["Authorization"] = token?.access_token;
  const resp = await axios(options);
  return resp;
};

axios.interceptors.response.use(
  async (response) => {
    if (response.data.data) {
      if (response.config.url.includes("validate-otp")) {
        const authKey = response.data.data.authKey;
        if (authKey?.access_token && authKey?.refresh_token) {
          const token = {
            access_token: authKey.access_token,
            refresh_token: authKey.refresh_token,
          };
            CookieUtils.set("token", token, 1);
        }
        if (authKey?.clientName) {
          CookieUtils.set("clientName", authKey.clientName, 1);
        }
        if (authKey?.roleFeatureList) {
          CookieUtils.set("roleFeatureList", authKey.roleFeatureList, 1);
          localStorage.setItem("roleFeatureList", JSON.stringify(authKey.roleFeatureList));
        }
      }
    }
    const username = CookieUtils.get("username");
    const token = CookieUtils.get("token");
    if (token && !username) {
      CookieUtils.removeAll();
      window.location.href = "/login";
      return Promise.reject();
    }
    return response;
  },
  async (error) => {
    try {
      const { status, config } = error.response;
      if (
        (status === 401 || status === 403) &&
        config.url.includes("user/login")
      ) {
        return Promise.reject(error);
      }
      if (config.url.includes("user/login")) {
        return Promise.reject(error);
      }
      if (status === 401 && config.url.includes("sign-in")) {
        return Promise.reject(error);
      }
      if (
        status === 401 ||
        (status === 403 && config.url.includes !== "user/login")
      ) {
        if (
          (status === 401 || status === 403) &&
          config.url.includes("refresh-token")
        ) {
          CookieUtils.remove("token");
          CookieUtils.remove("clientName");
          window.location.href = "/login";
        } else {
          const release = await mutex.acquire();
          let tokenData = getAuthToken();
          let { access_token, refresh_token } = tokenData || {};
          let retry = false;
          try {
            if (
              access_token != null &&
              access_token !== error.config.headers.Authorization
            ) {
              retry = true;
            } else {
              if (refresh_token) {
                const data = { refresh_token: refresh_token };
                const option = {
                  method: "post",
                  url: baseURL + "/refresh-token",
                  data,
                };
                const resp = await axios(option);
                if (
                  resp &&
                  resp.status === 200 &&
                  resp.data.data &&
                  resp.data.data.access_token !== ""
                ) {
                  const token = {
                    access_token: resp.data.data.access_token,
                    refresh_token: resp.data.data.refresh_token,
                  };
                  CookieUtils.set("token", token, 1);
                  retry = true;
                }
                if (resp && (resp.code === 401 || resp.code === 403)) {
                  return sessionLogout();
                }
              }
            }
          } finally {
            release();
          }
          if (retry) {
            const resp = await onRetry(error.response);
            return Promise.resolve(resp);
          }
        }
      }
      return Promise.reject(error);
    } catch (err) {
      return Promise.reject(err);
    }
  }
);

axios.interceptors.request.use(
  (request) => {
    const token = getAuthToken();
    if (token && token.access_token) {
      request.headers["auth-key"] = token.access_token;
    }
    return request;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export { CookieUtils, getAuthToken };
